import { signOut } from 'firebase/auth';
import i18next from 'i18next';
import { LogOutIcon } from 'lucide-react';
import { useContext } from 'react';

import { AuthContext } from '../auth/AuthContext';
import { auth } from '../auth/firebase';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './dropdown-menu';

const getInitials = (displayName: string | null) => {
  if (!displayName) return '';
  // Split the name into words and filter out any empty strings.
  const words = displayName.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return '';

  // If there's only one word, use up to the first two characters.
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  // Otherwise, take the first letter of the first two words.
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const GoogleAvatar = () => {
  const { user } = useContext(AuthContext);

  const handleSignOut = async () => signOut(auth);

  if (!user) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="cursor-pointer group inline-block rounded-full hover:bg-gray-100 transition-colors duration-200 p-1">
          <Avatar className="group-hover:ring-2 group-hover:ring-blue-500 transition duration-200">
            {user.photoURL && <AvatarImage src={user.photoURL} />}
            <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
          </Avatar>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 ml-2">
        <DropdownMenuLabel>{user.displayName}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleSignOut}>
          <LogOutIcon className="h-4 w-4" />
          <span>{i18next.t('sign_in.sign_out')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { GoogleAvatar };
